import { supabase } from "@/integrations/supabase/client";

// Define a simplified type to avoid deep type instantiation issues
export interface SimpleFundRecord {
  id: string;
  fund_name: string;
  company_name?: string | null;
  company_id?: string | null;
  fund_type?: string | null;
  asset_class?: string | null;
  fund_code?: string | null;
  currency?: string | null;
  category?: string | null;
  management_fee?: string | null;
  performance_fee?: string | null;
  fund_status?: string | null;
  fund_marketing_url?: string | null;
  inception_date?: string | null;
  management_company_code?: string | null;
  [key: string]: any; // Allow for additional properties
}

// Use specific types rather than complex generic types
export type FundDetailsResult = {
  company_id: string | null;
  company_name?: string | null;
};

// Explicit response type for better type inference
export type CompanyFundsQueryResult = {
  data: SimpleFundRecord[];
  count: number;
};

// Utility function to normalize website URLs
export function normalizeWebsiteUrl(url: string | null | undefined): string {
  if (!url) return '';
  
  // Remove protocol, www, trailing slashes, and query parameters
  const normalized = url.toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '')
    .split('?')[0]
    .split('#')[0];
    
  return normalized;
}

// Track which query strategy worked last to optimize pagination
let lastSuccessfulStrategy: 'company_id' | 'company_name' | 'website_url' | null = null;

// Helper function to fetch the fund details (including company_id)
export async function fetchFundDetails(fundId: string | undefined): Promise<FundDetailsResult> {
  if (!fundId) {
    console.error('No fundId provided to fetchFundDetails');
    return { company_id: null };
  }
  
  try {
    // Try to get company_id from funds table
    console.log('Querying funds table for fund details...');
    
    const { data: fundsData, error } = await supabase
      .from('funds')
      .select('id, company_id')
      .eq('id', fundId)
      .single();
    
    if (error) {
      console.error('Supabase error fetching from funds:', error);
      // Instead of throwing, continue to fallback
    } else if (fundsData) {
      console.log('funds table result: data found');
      // If we have valid company_id directly
      if (fundsData.company_id) {
        return { 
          company_id: fundsData.company_id,
          company_name: undefined
        };
      }
    }
    
    // Fallback to funds_raw table
    console.log('No result in funds table, trying funds_raw...');
    const { data: rawData, error: rawFallbackError } = await supabase
      .from('funds_raw')
      .select('id, company_id, management_company_code')
      .eq('id', fundId)
      .single();
    
    if (rawFallbackError) {
      console.error('Supabase error fetching from funds_raw fallback:', rawFallbackError);
      return { company_id: null };
    }
    
    // Handle the fallback data safely with explicit null check and type safety
    if (!rawData) {
      return { company_id: null };
    }
    
    // Type safety: create a typed object to access properties safely
    const rawFallbackData = rawData as {
      id?: string;
      company_id?: string;
      management_company_code?: string;
    };
    
    // Try the company_id first with proper null checks
    if (rawFallbackData.company_id && typeof rawFallbackData.company_id === 'string') {
      console.log('Found company_id in funds_raw:', rawFallbackData.company_id);
      return { company_id: rawFallbackData.company_id };
    }
    
    // Then try the management_company_code with proper null checks
    if (rawFallbackData.management_company_code && typeof rawFallbackData.management_company_code === 'string') {
      console.log('Found management_company_code in funds_raw:', rawFallbackData.management_company_code);
      return { company_id: rawFallbackData.management_company_code };
    }
    
    // If we got here, no valid data was found
    return { company_id: null };
    
    // No company_id found in either table
    console.log('No company_id found for fund:', fundId);
    return { company_id: null };
  } catch (err) {
    console.error('Unexpected error in fetchFundDetails:', err);
    return { company_id: null };
  }
}

// Helper function to fetch all funds for a company with pagination
export async function fetchCompanyFunds(
  params: { 
    company_id: string | null | undefined; 
    excludeFundId: string;
    page: number;
    pageSize: number;
    companyName?: string | null;
    websiteUrl?: string | null;
  }
): Promise<CompanyFundsQueryResult> {
  // Extract params to local variables for cleaner code
  const { company_id, excludeFundId, page, pageSize, companyName, websiteUrl } = params;

  // Error handling for missing company identifiers
  if (!company_id && !companyName && !websiteUrl) {
    console.error('No company identifier provided (id, name, or website) to fetchCompanyFunds');
    return { data: [], count: 0 };
  }

  try {
    // Pagination calculation
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    
    // Determine which strategy to try first based on previous success
    let responseData: any[] = [];
    let responseCount = 0;
    
    // Logic to decide which strategy to try first based on what worked last time
    // This helps with pagination by trying the successful method first
    const hasCompanyId = typeof company_id === 'string' && company_id.trim() !== '';
    const hasCompanyName = companyName && typeof companyName === 'string' && companyName.trim() !== '';
    const hasWebsiteUrl = websiteUrl && typeof websiteUrl === 'string' && websiteUrl.trim() !== '';
    
    // Log the strategy selection process
    console.log(`Strategy selection - Last successful: ${lastSuccessfulStrategy || 'none'}, Has company_id: ${hasCompanyId}, Has name: ${hasCompanyName}, Has URL: ${hasWebsiteUrl}`);
    
    // STRATEGY 1: Direct company_id query (either as first choice or if it was last successful)
    if ((lastSuccessfulStrategy === 'company_id' || lastSuccessfulStrategy === null) && hasCompanyId) {
      console.log(`Querying funds with company_id: ${company_id}`);
      
      // Create a base query without adding filters that could cause type instantiation issues
      console.log(`Building query with company_id: ${company_id}`);
      let baseQuery = supabase
        .from('funds_raw')
        .select(
          'id, fund_name, company_id, company_name, management_company_code, fund_type, sa_asset_class, currency, fund_code, category, management_fee, fund_marketing_url, created_at',
          { count: 'exact' }
        )
        .neq('id', excludeFundId)
        .range(from, to)
        .order('fund_name', { ascending: true });
        
      // Apply company filtering with raw string parameters instead of method chaining
      console.log('Applying company filters with simple string parameters');
      baseQuery = baseQuery.or(`company_id.eq.${company_id},management_company_code.eq.${company_id}`);
      
      // Execute the query with pagination and ordering
      console.log('Executing funds_raw query with range', { from, to });
      const response = await baseQuery;
      
      // Handle errors
      if (response.error) {
        console.error('Supabase query error on funds table:', response.error);
      } else {
        // Get count and data from response
        responseData = response.data || [];
        responseCount = response.count || 0;
        
        // Log results
        console.log(`Found ${responseCount} related funds for company ${company_id} (displaying ${responseData.length})`);
        
        // Update the last successful strategy if we got results
        if ((responseData?.length || 0) > 0) {
          lastSuccessfulStrategy = 'company_id';
          console.log('company_id strategy was successful, saving for future pagination');
        }
      }
    }
    
    // STRATEGY 2: Name-based fallback (either as first choice if it was last successful, or as fallback)
    if ((responseData?.length || 0) === 0 && hasCompanyName && lastSuccessfulStrategy !== 'website_url') {
      if (lastSuccessfulStrategy !== 'company_name') {
        console.log(`No results found using company_id. Trying name-based fallback with company name: "${companyName}"`);
      } else {
        console.log(`Using previously successful name-based search with company name: "${companyName}"`);
      }
      
      try {
        // Use fuzzy search on company_name instead
        const nameResponse = await supabase
          .from('funds_raw')
          .select(
            'id, fund_name, company_id, company_name, management_company_code, fund_type, sa_asset_class, currency, fund_code, category, management_fee, fund_marketing_url, created_at',
            { count: 'exact' }
          )
          .neq('id', excludeFundId)
          .ilike('company_name', `%${companyName}%`) // Case-insensitive partial match
          .order('fund_name', { ascending: true })
          .range(from, to);
          
        if (nameResponse.error) {
          console.error('Error in name-based fallback search:', nameResponse.error);
        } else if (nameResponse.data && (nameResponse.data?.length || 0) > 0) {
          // We found results with the name-based search!
          responseData = nameResponse.data;
          responseCount = nameResponse.count || 0;
          lastSuccessfulStrategy = 'company_name';
          console.log(`Found ${responseCount} funds via name-based search for "${companyName}"`);
          console.log('company_name strategy was successful, saving for future pagination');
        } else {
          console.log(`Name-based fallback found no results for "${companyName}"`);
        }
      } catch (fuzzyError) {
        console.error('Error in name-based fallback search:', fuzzyError);
      }
    }
    
    // STRATEGY 3: Website-based search (either as first choice if it was last successful, or as fallback)
    if (((responseData?.length || 0) === 0 || lastSuccessfulStrategy === 'website_url') && hasWebsiteUrl) {
      const normalizedSiteUrl = websiteUrl ? normalizeWebsiteUrl(websiteUrl) : '';
      if (!normalizedSiteUrl) {
        console.log('No valid website URL provided for fallback');
        // Return a properly typed empty array instead of unsafe casting
        return { data: [], count: 0 };
      }
      
      // Log whether this is our primary strategy or fallback
      if (lastSuccessfulStrategy === 'website_url') {
        console.log(`Using previously successful website-based search with URL: "${normalizedSiteUrl}"`);
      } else {
        console.log(`Trying website-based fallback with URL: "${normalizedSiteUrl}"`);
      }
      
      try {
        // Fetch all fund companies with their websites for comparison
        // fund_companies table does have website_url field (unlike funds_raw)
        const { data: fundCompaniesData, error: fundCompaniesError } = await supabase
          .from('fund_companies')
          .select('id, website_url')
          .not('website_url', 'is', null)
          .ilike('website_url', `%${normalizedSiteUrl}%`); // Use ilike for more flexible matching
          
        if (fundCompaniesError) {
          console.error('Error fetching fund_companies for URL matching:', fundCompaniesError);
        } else if (fundCompaniesData && (fundCompaniesData?.length || 0) > 0) {
          const matchingCompanies: { id: string }[] = fundCompaniesData;
          if ((matchingCompanies?.length || 0) > 0) {
            console.log(`Found ${matchingCompanies.length} companies with matching website: ${normalizedSiteUrl}`);
            
            // Get the IDs of matching companies
            const matchingIds = matchingCompanies.map(company => company.id);
            
            // Query funds using these IDs
            const websiteFrom = (page - 1) * pageSize;
            const websiteTo = websiteFrom + pageSize - 1;
            
            // First do a COUNT query to get accurate total count for pagination
            // Using a simplified approach to avoid TypeScript deep instantiation errors
            let totalCount = 0;
            if ((matchingIds?.length || 0) > 0) {
              try {
                // Build a count query using raw SQL filter pattern to avoid type instantiation issues
                const companyIdsFilter = `company_id.in.(${matchingIds.join(',')})`;
                
                // Execute as two separate steps to avoid complex type chaining
                const countQueryBase = supabase.from('funds_raw').select('id', { count: 'exact', head: true });
                
                // Apply the filters separately
                const { count, error } = await countQueryBase
                  .not('id', 'eq', excludeFundId)
                  .or(companyIdsFilter);
                  
                if (!error && count !== null) {
                  totalCount = count;
                  console.log(`Total count of matching funds: ${totalCount}`);
                } else if (error) {
                  console.error('Error getting count:', error);
                }
              } catch (countError) {
                console.error('Exception in count query:', countError);
              }
            }
            
            // Now perform the actual data query with pagination using the same pattern
            // that worked for the count query to avoid TypeScript errors
            let websiteData = { data: [], error: null };
            
            if ((matchingIds?.length || 0) > 0) {
              try {
                // Build query using same pattern as count query
                const companyIdsFilter = `company_id.in.(${matchingIds.join(',')})`;                
                
                // Create the base query with all needed fields
                const dataQueryBase = supabase
                  .from('funds_raw')
                  .select(
                    'id, fund_name, company_id, company_name, management_company_code, fund_type, sa_asset_class, currency, fund_code, category, management_fee, fund_marketing_url, created_at'
                  )
                  .order('fund_name', { ascending: true })
                  .range(websiteFrom, websiteTo);
                
                // Apply filters separately to avoid deep type instantiation
                websiteData = await dataQueryBase
                  .not('id', 'eq', excludeFundId)
                  .or(companyIdsFilter);
                  
                console.log(`Data query returned ${websiteData.data?.length || 0} results with pagination`);
              } catch (dataQueryError) {
                console.error('Exception in data query:', dataQueryError);
              }
            }
              
            // Create a response object with accurate count
            const websiteResponse = {
              data: websiteData.data || [],
              count: totalCount,
              error: websiteData.error
            };
            
            if (websiteResponse.error) {
              console.error('Supabase error in website-based fallback:', websiteResponse.error);
            } else if ((websiteResponse.data?.length || 0) > 0) {
              // We found results with the website-based search!
              console.log(`Website-based search found ${websiteResponse.count || 0} funds for URL "${normalizedSiteUrl}" (displaying ${websiteResponse.data.length})`);
              
              // Use these results instead
              responseData = websiteResponse.data;
              responseCount = websiteResponse.count || 0;
              
              // Save this as the successful strategy for future pagination requests
              lastSuccessfulStrategy = 'website_url';
              console.log(`Found ${responseCount} funds via website URL match`);
              console.log('website_url strategy was successful, saving for future pagination');
            } else {
              console.log(`Website-based search found no results for URL "${normalizedSiteUrl}"`);
            }
          } else {
            console.log(`No companies found with matching website: ${normalizedSiteUrl}`);
          }
        }
      } catch (websiteError) {
        console.error('Error in website-based fallback search:', websiteError);
      }
    }
    
    // If we've tried all strategies and still found nothing, log a warning
    if ((responseData?.length || 0) === 0) {
      console.warn('No funds found after trying all available strategies');
      // Reset strategy tracking since we have no successful strategy
      lastSuccessfulStrategy = null;
    } else {
      console.log(`Successfully found ${responseData.length} funds using strategy: ${lastSuccessfulStrategy}`);
    }

    // Transform raw DB records to typed SimpleFundRecord objects
    const simpleFunds = responseData.map(record => {
      if (!record || typeof record !== 'object') {
        return { id: '', fund_name: '' } as SimpleFundRecord;
      }
      
      // Map database fields to our interface fields using snake_case to match the interface
      return {
        id: record.id || '',
        fund_name: record.fund_name || 'Unnamed Fund',
        company_name: record.company_name || 'Unknown Company',
        company_id: record.company_id || null,
        fund_type: record.fund_type || null,
        asset_class: record.sa_asset_class || null,
        fund_code: record.fund_code || null,
        currency: record.currency || null,
        category: record.category || null,
        management_fee: record.management_fee || null,
        fund_marketing_url: record.fund_marketing_url || null,
        inception_date: record.created_at || null,
        management_company_code: record.management_company_code || null
      } as SimpleFundRecord;
    });
    
    // Log the mapped results for debugging
    console.log(`Mapped ${simpleFunds.length} funds after field normalization`);
    if ((simpleFunds?.length || 0) > 0) {
      console.log('First fund sample after mapping:', simpleFunds[0]);
    }
    
    // Return the final result with accurate count for pagination
    console.log(`Returning ${simpleFunds.length} typed fund records with total count ${responseCount} using strategy: ${lastSuccessfulStrategy || 'none'}`);
    return { 
      data: simpleFunds,
      count: responseCount
    };
  } catch (error) {
    console.error('Error in fetchCompanyFunds:', error);
    // Reset strategy tracking on catastrophic errors
    lastSuccessfulStrategy = null;
    return { data: [], count: 0 }; // Return empty data instead of throwing
  }
}

// Function to reset the query strategy tracking
// This can be useful when component unmounts or when props change drastically
export function resetQueryStrategy(): void {
  lastSuccessfulStrategy = null;
  console.log('Query strategy tracking reset');
}

// Function to get the current strategy (for testing/debugging)
export function getQueryStrategy(): string | null {
  return lastSuccessfulStrategy;
}

// Function to get the current strategy (for testing/debugging)
export function getCurrentStrategy(): string | null {
  return lastSuccessfulStrategy;
}
